import { useFormContext, useWatch } from "react-hook-form";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

import { TIPOS_DOCUMENTO } from "@/config/form-options";
import type { StepId } from "@/features/multistep/registry/types";
import { Paises } from "@/enum/paises.enum";
import { useLocations } from "@/hooks/use-location";
import { useState } from "react";

export function RepresentanteLegalStep({ stepId }: { stepId: StepId }) {
  const { control } = useFormContext();
  const [paisId, setPaisId] = useState<string | undefined>();
  const [departamentoId, setDepartamentoId] = useState<string | undefined>();

  const tipoDocumento = useWatch({
    control,
    name: "informacionBasica.tipoDocumento",
  });
  const pais = useWatch({
    control,
    name: `${stepId}.pais`,
  });
  const departamento = useWatch({
    control,
    name: `${stepId}.departamento`,
  });

  const { paises, departamentos, ciudades } = useLocations(
    paisId,
    departamentoId
  );

  if (tipoDocumento !== "NIT") {
    return (
      <div className="p-6 text-center text-gray-500 border rounded-xl">
        Este paso no aplica ya que el tipo de documento no es NIT.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
      {/* Tipo de documento */}
      <FormField
        control={control}
        name={`${stepId}.tipoDocumento`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Tipo de documento</FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Selecciona un tipo" />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_DOCUMENTO.filter((t) => t.value !== "NIT").map(
                    (doc) => (
                      <SelectItem key={doc.value} value={doc.value}>
                        {doc.label}
                      </SelectItem>
                    )
                  )}
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {/* No. Documento */}
      <FormField
        control={control}
        name={`${stepId}.numeroDocumento`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>No. Documento</FormLabel>
            <FormControl>
              <Input {...field} placeholder="Número de documento" />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {/* Nombres */}
      <FormField
        control={control}
        name={`${stepId}.nombres`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Nombres</FormLabel>
            <FormControl>
              <Input {...field} placeholder="Nombres" />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {/* Apellidos */}
      <FormField
        control={control}
        name={`${stepId}.apellidos`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Apellidos</FormLabel>
            <FormControl>
              <Input {...field} placeholder="Nombres" />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {/* Lugar de nacimiento → País */}
      <FormField
        control={control}
        name={`${stepId}.pais`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>País</FormLabel>
            <FormControl>
              <Select
                onValueChange={(label) => {
                  const selected = paises.find((p) => p.label === label);
                  if (selected) {
                    setPaisId(selected.value); // este es el que viaja a tu API
                  }

                  // Si quieres que el form guarde el id real
                  field.onChange(label ?? "");
                }}
                value={field.value}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Selecciona un país" />
                </SelectTrigger>
                <SelectContent>
                  {paises.map((p) => (
                    <SelectItem key={p.value} value={p.label}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {/* Departamento */}
      {pais === Paises.Colombia && (
        <FormField
          control={control}
          name={`${stepId}.departamento`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Departamento</FormLabel>
              <FormControl>
                <Select
                  onValueChange={(label) => {
                    const selected = departamentos.find(
                      (d) => d.label === label
                    );
                    if (selected) {
                      setDepartamentoId(selected.value); // este es el que viaja a tu API
                    }

                    // Si quieres que el form guarde el id real
                    field.onChange(label ?? "");
                  }}
                  value={field.value}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Selecciona un departamento" />
                  </SelectTrigger>
                  <SelectContent>
                    {departamentos.map((d) => (
                      <SelectItem key={d.value} value={d.label}>
                        {d.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
      {/* Ciudad */}
      <FormField
        control={control}
        name={`${stepId}.ciudad`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Ciudad</FormLabel>
            <FormControl>
              {pais === Paises.Colombia && departamento ? (
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Selecciona una ciudad" />
                  </SelectTrigger>
                  <SelectContent>
                    {(ciudades ?? []).map((c) => (
                      <SelectItem key={c.value} value={c.label}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  {...field}
                  placeholder="Ingresa la ciudad"
                  className="w-full"
                />
              )}
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {/* Fecha de nacimiento */}
      <FormField
        control={control}
        name={`${stepId}.fechaNacimiento`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Fecha de nacimiento</FormLabel>
            <FormControl>
              <Input type="date" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {/* Dirección */}
      <FormField
        control={control}
        name={`${stepId}.direccion`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Dirección</FormLabel>
            <FormControl>
              <Input {...field} placeholder="Dirección" />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {/* Teléfono */}
      <FormField
        control={control}
        name={`${stepId}.telefono`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Teléfono</FormLabel>
            <FormControl>
              <Input {...field} placeholder="Teléfono" />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {/* Ocupación */}
      <FormField
        control={control}
        name={`${stepId}.ocupacion`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Ocupación / oficio / profesión</FormLabel>
            <FormControl>
              <Input {...field} placeholder="Ej: Abogado" />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
